
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const resources = [
  {
    category: "Research Archives",
    items: [
      {
        title: "Houston Metropolitan Research Center",
        description: "Extensive collection of Houston history, maps, and photographs",
        url: "https://houstonlibrary.org/research/hmrc",
        type: "external"
      },
      {
        title: "Harris County Public Library",
        description: "Local history collection and digital archives",
        url: "https://www.hcpl.net/",
        type: "external"
      },
      {
        title: "Houston Archaeological & Historical Commission",
        description: "Official historical landmark designations and documentation",
        url: "https://www.houstontx.gov/planning/HistoricPres/",
        type: "external"
      }
    ]
  },
  {
    category: "City Planning & Maps",
    items: [
      {
        title: "City of Houston GIS Maps",
        description: "Interactive maps showing neighborhoods, parks, and infrastructure",
        url: "https://cohgis.houstontx.gov/",
        type: "external"
      },
      {
        title: "Houston Parks & Recreation",
        description: "Official park information, trails, and facilities",
        url: "https://www.houstontx.gov/parks/",
        type: "external"
      },
      {
        title: "Metro Transit Maps",
        description: "Public transportation routes and schedules",
        url: "https://www.ridemetro.org/",
        type: "external"
      }
    ]
  },
  {
    category: "Local Organizations",
    items: [
      {
        title: "Houston Wilderness",
        description: "Conservation and outdoor recreation information",
        url: "https://houstonwilderness.org/",
        type: "external"
      },
      {
        title: "Preservation Houston",
        description: "Historic preservation advocacy and building information",
        url: "https://www.preservationhouston.org/",
        type: "external"
      },
      {
        title: "Buffalo Bayou Partnership",
        description: "Bayou conservation, park programming, and trail information",
        url: "https://buffalobayou.org/",
        type: "external"
      }
    ]
  },
  {
    category: "Cultural Resources",
    items: [
      {
        title: "Houston Museum District",
        description: "19 museums and cultural institutions in one walkable area",
        url: "https://www.houstonmuseumdistrict.org/",
        type: "external"
      },
      {
        title: "Houston Arts Alliance",
        description: "Public art, cultural events, and neighborhood art programs",
        url: "https://www.houstonartsalliance.com/",
        type: "external"
      },
      {
        title: "Visit Houston",
        description: "Official tourism information and event listings",
        url: "https://www.visithoustontexas.com/",
        type: "external"
      }
    ]
  }
];

const tools = [
  {
    title: "AllTrails",
    description: "Trail maps and user reviews for Houston area hiking and walking trails",
    category: "Navigation",
    url: "https://www.alltrails.com/"
  },
  {
    title: "iNaturalist",
    description: "Identify plants and wildlife you encounter on urban nature walks",
    category: "Nature",
    url: "https://www.inaturalist.org/"
  },
  {
    title: "Google Street View",
    description: "Preview routes and check accessibility before visiting",
    category: "Planning",
    url: "https://www.google.com/streetview/"
  },
  {
    title: "Weather Underground",
    description: "Hyperlocal Houston weather for optimal timing of outdoor activities",
    category: "Planning",
    url: "https://www.wunderground.com/"
  }
];

export default function Resources() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Houston Research Resources & Planning Tools",
    "description": "Research sources, planning tools, and organizations that help tell Houston's stories. These resources inform the guides and support independent exploration.",
    "url": `${import.meta.env.VITE_SITE_URL}/resources`,
    "mainEntity": {
      "@type": "ItemList",
      "name": "Houston Research Resources",
      "itemListElement": resources.flatMap((category, categoryIndex) =>
        category.items.map((item, itemIndex) => ({
          "@type": "ListItem",
          "position": categoryIndex * 10 + itemIndex + 1,
          "item": {
            "@type": "WebSite",
            "name": item.title,
            "description": item.description,
            "url": item.url
          }
        }))
      )
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <SEO
        title="Houston Research Resources & Planning Tools - Eric Javidi"
        description="Research sources, planning tools, and organizations that help tell Houston's stories. Houston Metropolitan Research Center, city planning resources, and local organizations."
        keywords="Houston research, Houston archives, Houston Metropolitan Research Center, Houston planning, Buffalo Bayou Partnership, Preservation Houston"
        ogTitle="Houston Research Resources & Planning Tools - Eric Javidi"
        ogDescription="Research sources, planning tools, and organizations that help tell Houston's stories."
        ogUrl={`${import.meta.env.VITE_SITE_URL}/resources`}
        twitterTitle="Houston Research Resources & Planning Tools - Eric Javidi"
        twitterDescription="Research sources, planning tools, and organizations that help tell Houston's stories."
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="bg-gradient-to-r from-emerald-900 via-teal-900 to-emerald-800 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-bold text-white flex items-center">
                <div className="w-8 h-8 bg-emerald-400 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-compass-3-line text-emerald-900"></i>
                </div>
                Eric Javidi
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <Link to="/" className="text-emerald-200 hover:text-emerald-100 px-3 py-2 text-sm font-medium transition-colors">
                  Home
                </Link>
                <Link to="/about" className="text-emerald-200 hover:text-emerald-100 px-3 py-2 text-sm font-medium transition-colors">
                  About
                </Link>
                <Link to="/guides" className="text-emerald-200 hover:text-emerald-100 px-3 py-2 text-sm font-medium transition-colors">
                  Guides
                </Link>
                <Link to="/resources" className="text-white bg-emerald-700 rounded-md px-3 py-2 text-sm font-medium">
                  Resources
                </Link>
                <Link to="/contact" className="text-emerald-200 hover:text-emerald-100 px-3 py-2 text-sm font-medium transition-colors">
                  Contact
                </Link>
              </div>
            </div>
            <div className="md:hidden">
              <button className="text-emerald-200 hover:text-white">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header Hero */}
      <section
        className="py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 relative overflow-hidden"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/68af18c565816fa8c6a1213416efdbf8.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-800/90 via-teal-800/85 to-emerald-700/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <i className="ri-database-line text-4xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
              Resources &amp; Tools
            </h1>
            <p className="text-xl text-emerald-100 leading-relaxed">
              Research sources, planning tools, and organizations that help tell Houston's stories. These resources inform the guides and support independent exploration.
            </p>
          </div>
        </div>
      </section>

      {/* Research Resources */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Research Sources
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto"></div>
          </div>

          <div className="space-y-16">
            {resources.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <div className="flex items-center justify-center mb-10">
                  <div className="flex items-center bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-4 rounded-full shadow-lg">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <i className={`text-2xl ${
                        category.category === 'Research Archives' ? 'ri-book-open-line' :
                        category.category === 'City Planning & Maps' ? 'ri-map-2-line' :
                        category.category === 'Local Organizations' ? 'ri-community-line' :
                        'ri-palette-line'
                      }`}></i>
                    </div>
                    <h3 className="text-2xl font-bold">{category.category}</h3>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {category.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="group">
                      <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                        <div className={`h-48 bg-gradient-to-br ${
                          category.category === 'Research Archives' ? 'from-blue-400 to-blue-600' :
                          category.category === 'City Planning & Maps' ? 'from-green-400 to-green-600' :
                          category.category === 'Local Organizations' ? 'from-purple-400 to-purple-600' :
                          'from-pink-400 to-pink-600'
                        } relative overflow-hidden`}
                        style={{
                          backgroundImage: `url('${
                            item.title === 'Houston Metropolitan Research Center' ?
                              'https://readdy.ai/api/search-image?query=Houston%20Metropolitan%20Research%20Center%20interior%20with%20extensive%20historical%20archives%2C%20vintage%20documents%2C%20old%20maps%20and%20photographs%20organized%20in%20filing%20systems%2C%20professional%20archival%20storage%20with%20warm%20academic%20lighting%2C%20research%20library%20atmosphere%20with%20wooden%20shelves%20and%20study%20areas&width=400&height=300&seq=hmrc-card&orientation=landscape' :
                            item.title === 'Harris County Public Library' ?
                              'https://readdy.ai/api/search-image?query=Harris%20County%20Public%20Library%20modern%20interior%20with%20digital%20archives%20workstations%2C%20community%20members%20researching%20local%20history%2C%20contemporary%20library%20design%20with%20computers%20and%20book%20collections%2C%20bright%20welcoming%20atmosphere%20for%20public%20research&width=400&height=300&seq=hcpl-card&orientation=landscape' :
                            item.title === 'Houston Archaeological & Historical Commission' ?
                              'https://readdy.ai/api/search-image?query=Houston%20historic%20landmark%20building%20with%20official%20historical%20designation%20plaque%2C%20architectural%20preservation%20documentation%2C%20government%20building%20facade%20with%20historical%20significance%2C%20professional%20heritage%20preservation%20setting&width=400&height=300&seq=hahc-card&orientation=landscape' :
                            item.title === 'City of Houston GIS Maps' ?
                              'https://readdy.ai/api/search-image?query=Houston%20city%20planning%20office%20with%20large%20detailed%20GIS%20maps%20displayed%20on%20screens%2C%20urban%20development%20blueprints%20modern%20mapping%20technology%20workstations%20aerial%20photographs%20of%20Houston%20neighborhoods%20and%20infrastructure%20planning%20tools&width=400&height=300&seq=gis-card&orientation=landscape' :
                            item.title === 'Houston Parks & Recreation' ?
                              'https://readdy.ai/api/search-image?query=Houston%20public%20park%20with%20families%20enjoying%20recreational%20facilities%2C%20playground%20equipment%20green%20spaces%20with%20walking%20trails%20park%20amenities%20and%20community%20recreation%20areas%20sunny%20outdoor%20environment&width=400&height=300&seq=parks-card&orientation=landscape' :
                            item.title === 'Metro Transit Maps' ?
                              'https://readdy.ai/api/search-image?query=Houston%20Metro%20transit%20system%20with%20modern%20buses%20and%20light%20rail%2C%20public%20transportation%20maps%20and%20schedules%20commuters%20using%20transit%20services%20urban%20mobility%20infrastructure%20and%20transit%20stations&width=400&height=300&seq=metro-card&orientation=landscape' :
                            item.title === 'Houston Wilderness' ?
                              'https://readdy.ai/api/search-image?query=Houston%20wilderness%20area%20with%20native%20Texas%20plants%20and%20wildlife%2C%20conservation%20volunteers%20working%20in%20natural%20habitat%2C%20outdoor%20education%20programs%20pristine%20natural%20landscape%20with%20diverse%20ecosystems%20and%20environmental%20protection%20activities&width=400&height=300&seq=wilderness-card&orientation=landscape' :
                            item.title === 'Preservation Houston' ?
                              'https://readdy.ai/api/search-image?query=Historic%20Houston%20building%20undergoing%20preservation%20work%2C%20architectural%20conservation%20specialists%20documenting%20historic%20structures%2C%20vintage%20buildings%20with%20historical%20significance%20heritage%20preservation%20advocacy%20and%20restoration%20activities&width=400&height=300&seq=preservation-card&orientation=landscape' :
                            item.title === 'Buffalo Bayou Partnership' ?
                              'https://readdy.ai/api/search-image?query=Buffalo%20Bayou%20Houston%20with%20conservation%20teams%20working%20along%20the%20waterway%20park%20programming%20activities%20trail%20maintenance%20and%20ecological%20restoration%20natural%20bayou%20environment%20with%20downtown%20Houston%20skyline%20in%20background&width=400&height=300&seq=bayou-card&orientation=landscape' :
                            item.title === 'Houston Museum District' ?
                              'https://readdy.ai/api/search-image?query=Houston%20Museum%20District%20with%20multiple%20cultural%20institutions%2C%20visitors%20walking%20between%20museums%20modern%20and%20classical%20architecture%20educational%20and%20cultural%20atmosphere%20with%20diverse%20art%20installations%20and%20exhibits&width=400&height=300&seq=museum-district-card&orientation=landscape' :
                            item.title === 'Houston Arts Alliance' ?
                              'https://readdy.ai/api/search-image?query=Houston%20public%20art%20installation%20with%20community%20members%20enjoying%20cultural%20programming%20colorful%20murals%20and%20artistic%20displays%20neighborhood%20art%20programs%20and%20creative%20community%20events%20vibrant%20artistic%20atmosphere&width=400&height=300&seq=arts-card&orientation=landscape' :
                              'https://readdy.ai/api/search-image?query=Visit%20Houston%20tourism%20center%20with%20diverse%20visitors%20exploring%20Houston%20attractions%20welcome%20center%20with%20tourist%20information%20displays%20multicultural%20group%20planning%20Houston%20activities%20friendly%20tourism%20atmosphere&width=400&height=300&seq=visit-houston-card&orientation=landscape'
                          }')`
                        }}>
                          <div className={`absolute inset-0 bg-gradient-to-br ${
                            category.category === 'Research Archives' ? 'from-blue-600/80 to-blue-800/80' :
                            category.category === 'City Planning & Maps' ? 'from-green-600/80 to-green-800/80' :
                            category.category === 'Local Organizations' ? 'from-purple-600/80 to-purple-800/80' :
                            'from-pink-600/80 to-pink-800/80'
                          }`}></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                              <i className={`text-3xl text-white ${
                                category.category === 'Research Archives' ? 'ri-archive-line' :
                                category.category === 'City Planning & Maps' ? 'ri-road-map-line' :
                                category.category === 'Local Organizations' ? 'ri-team-line' :
                                'ri-artboard-line'
                              }`}></i>
                            </div>
                          </div>
                        </div>

                        <div className="p-6">
                          <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors">
                            {item.title}
                          </h4>
                          <p className="text-gray-600 mb-6 leading-relaxed">
                            {item.description}
                          </p>
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-full hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
                          >
                            Visit Resource
                            <i className="ri-external-link-line ml-2"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planning Tools */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Planning Tools
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-emerald-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Essential digital tools to enhance your Houston exploration experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tools.map((tool, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                  <div className={`h-32 bg-gradient-to-br ${
                    tool.category === 'Navigation' ? 'from-blue-400 to-indigo-500' :
                    tool.category === 'Nature' ? 'from-green-400 to-emerald-500' :
                    'from-orange-400 to-red-500'
                  } relative`}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className={`text-3xl text-white ${
                          tool.category === 'Navigation' ? 'ri-navigation-line' :
                          tool.category === 'Nature' ? 'ri-leaf-line' :
                          'ri-calendar-line'
                        }`}></i>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors">
                      {tool.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {tool.description}
                    </p>
                    <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4 ${
                      tool.category === 'Navigation' ? 'bg-blue-100 text-blue-800' :
                      tool.category === 'Nature' ? 'bg-green-100 text-green-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {tool.category}
                    </span>
                    <div>
                      <a
                        href={tool.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-full hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
                      >
                        Visit Tool
                        <i className="ri-external-link-line ml-2"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Local Experts */}
      <section className="py-20 bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20community%20meeting%20with%20diverse%20group%20of%20local%20experts%2C%20historians%2C%20urban%20planners%2C%20and%20naturalists%20discussing%20around%20a%20table%20with%20Houston%20maps%20and%20documents%2C%20warm%20collaborative%20atmosphere%20professional%20networking%20environment&width=1200&height=600&seq=experts-bg&orientation=landscape')`
          }}
        ></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-12">
            <div className="w-20 h-20 bg-emerald-500/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-group-line text-4xl text-emerald-400"></i>
            </div>
            <h2 className="text-4xl font-bold mb-6">
              Local Knowledge Network
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              These guides are informed by conversations with neighborhood historians, urban planners, naturalists, and longtime residents who generously share their expertise and stories.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-group-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Neighborhood Historians</h3>
              <p className="text-gray-300 leading-relaxed">
                Local residents and community leaders who preserve and share neighborhood stories
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Urban Planners</h3>
              <p className="text-gray-300 leading-relaxed">
                City planning professionals who understand Houston's development patterns
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-plant-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Naturalists</h3>
              <p className="text-gray-300 leading-relaxed">
                Environmental educators and conservationists working in Houston's green spaces
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contributing */}
      <section className="py-20 bg-gradient-to-br from-emerald-100 via-teal-50 to-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-12 border border-emerald-100">
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-8">
              <i className="ri-lightbulb-line text-4xl text-white"></i>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Know Something We Should Include?
            </h2>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              Houston's story is constantly evolving. If you know of resources, corrections, or additional context that would improve these guides, please share.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-lg font-semibold rounded-full hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Share Your Knowledge
              <i className="ri-mail-line ml-3 text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-compass-3-line text-white text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold">Eric Javidi</h3>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Houston-based storyteller exploring where the city's history meets its natural landscapes.
              </p>
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.youtube.com/@EricJavidi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-red-400 transition-colors"
                  aria-label="YouTube Channel"
                >
                  <i className="ri-youtube-fill text-2xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-6 text-lg">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-300 hover:text-emerald-400 transition-colors">About</Link></li>
                <li><Link to="/guides" className="text-gray-300 hover:text-emerald-400 transition-colors">Guides</Link></li>
                <li><Link to="/resources" className="text-gray-300 hover:text-emerald-400 transition-colors">Resources</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-emerald-400 transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-6 text-lg">Connect</h4>
              <ul className="space-y-3">
                <li><a href="https://www.youtube.com/@EricJavidi" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-emerald-400 transition-colors">YouTube Channel</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="text-gray-300 hover:text-emerald-400 transition-colors">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 Eric Javidi. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
